import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Upload, Building2 } from 'lucide-react';
import { registrationApi } from '../../lib/api';
import { slugEtablissement } from '../../../shared/identifiants.js';
import { TAUX_TVA_LEGAUX, tauxParDefaut } from '../../../shared/tauxTva.js';
import { CATEGORIES_FOURNISSEUR, JOURS_LIVRAISON } from '../../../shared/profilFournisseur.js';

interface ModuleProposeForm {
    name: string; label: string; description: string; statut: 'disponible' | 'bientot'; core: boolean; selectionnable: boolean; avec: string[];
    mode: 'gratuit' | 'fixe' | 'paliers'; prix_eur: number; paliers: { quantite: number; prix_eur: number }[];
}
const DOCUMENTS: { kind: string; label: string; aide: string; obligatoire: boolean }[] = [
    { kind: 'kbis', label: 'Extrait Kbis ou équivalent', aide: 'De moins de 3 mois. Entreprise individuelle : extrait K ou avis de situation Sirene.', obligatoire: true },
    { kind: 'identite', label: "Pièce d'identité du dirigeant", aide: 'Recto verso, en cours de validité.', obligatoire: true },
    { kind: 'rib', label: 'RIB de l’établissement', aide: 'Facultatif pour le moment.', obligatoire: false },
];
const euros = (n: number) => `${(Math.round(n * 100) / 100).toString().replace('.', ',')} €`;
/** Le prix mensuel d'un module, tel que le client le voit ; pour un module à paliers, le prix du plus petit palier. */
const prixMensuel = (m: ModuleProposeForm) => (m.mode === 'fixe' ? m.prix_eur : m.mode === 'paliers' && m.paliers.length ? Math.min(...m.paliers.map((p) => p.prix_eur)) : 0);
const libellePrix = (m: ModuleProposeForm) => (m.mode === 'gratuit' || prixMensuel(m) === 0 ? 'Gratuit' : `${m.mode === 'paliers' ? 'dès ' : ''}${euros(prixMensuel(m))} / mois`);

/** Formulaire public (aucune authentification) rempli par un futur client à partir du lien reçu par
 *  email — cf. bouton "Envoyer un formulaire d'inscription" du superadmin (AdminClientsView). Le jeton
 *  dans l'URL identifie la demande côté serveur ; il n'y a pas de session/JWT ici. */
export const ClientRegistrationForm: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [phase, setPhase] = React.useState<'loading' | 'form' | 'submitted' | 'invalid' | 'expired' | 'already'>('loading');
    const [clientType, setClientType] = React.useState<'restauration' | 'prestation' | 'hotellerie' | 'fournisseur' | 'commercial'>('restauration');
    const [locationMode, setLocationMode] = React.useState<'domicile' | 'entreprise' | 'les_deux'>('domicile');
    // Fournisseur : ce qu'il vend, où il livre, à quelles conditions (cf. shared/profilFournisseur.js).
    const [categories, setCategories] = React.useState<string[]>([]);
    const [autreActif, setAutreActif] = React.useState(false);
    const [autreTexte, setAutreTexte] = React.useState('');
    const [zone, setZone] = React.useState('');
    const [minimumHt, setMinimumHt] = React.useState('');
    const [joursLivraison, setJoursLivraison] = React.useState<string[]>([]);
    const [delaiHeures, setDelaiHeures] = React.useState('');
    const basculer = (liste: string[], valeur: string) => (liste.includes(valeur) ? liste.filter((v) => v !== valeur) : [...liste, valeur]);

    const [fullName, setFullName] = React.useState('');
    const [companyName, setCompanyName] = React.useState('');
    const [contactEmail, setContactEmail] = React.useState('');
    const [logoUrl, setLogoUrl] = React.useState('');
    const [logoFile, setLogoFile] = React.useState<File | null>(null);
    const [uploadingLogo, setUploadingLogo] = React.useState(false);
    const [tvaRates, setTvaRates] = React.useState<number[]>(tauxParDefaut('restauration'));
    const [tvaFranchise, setTvaFranchise] = React.useState(false);
    const [tvaConfirmed, setTvaConfirmed] = React.useState(false);
    const [modulesList, setModulesList] = React.useState<ModuleProposeForm[]>([]);
    const [selModules, setSelModules] = React.useState<string[]>([]);
    const [docs, setDocs] = React.useState<Record<string, string>>({});
    const [uploadingDoc, setUploadingDoc] = React.useState('');
    // Conditions posées par L'IAmani à l'invitation : une offre, ou la formule « client pilote ».
    const [conditions, setConditions] = React.useState<{ formule: 'standard' | 'pilote'; offre: { type: string; valeur: number; mois: number } | null; pilote: { mois: number; payants: string[] } | null }>({ formule: 'standard', offre: null, pilote: null });
    const [conditionsResume, setConditionsResume] = React.useState('');
    const [conventionOk, setConventionOk] = React.useState(false);
    const estPilote = conditions.formule === 'pilote' && !!conditions.pilote;
    const [address, setAddress] = React.useState('');
    const [postalCode, setPostalCode] = React.useState('');
    const [city, setCity] = React.useState('');
    const [country, setCountry] = React.useState('France');
    const [siret, setSiret] = React.useState('');
    const [tva, setTva] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [capital, setCapital] = React.useState('');
    const [ape, setApe] = React.useState('');
    const [siegeSocial, setSiegeSocial] = React.useState('');
    const [mediateur, setMediateur] = React.useState('');
    const [mediateurPlusTard, setMediateurPlusTard] = React.useState(false);
    const [rcsVille, setRcsVille] = React.useState('');
    const [rcsNumero, setRcsNumero] = React.useState('');

    const [error, setError] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (!token) { setPhase('invalid'); return; }
        registrationApi.getForm(token).then((data: any) => {
            if (data?.status === 'expired') { setPhase('expired'); return; }
            if (data?.status && data.status !== 'invited') { setPhase('already'); return; }
            const type = data?.client_type === 'prestation' ? 'prestation' : data?.client_type === 'hotellerie' ? 'hotellerie' : data?.client_type === 'fournisseur' ? 'fournisseur' : data?.client_type === 'commercial' ? 'commercial' : 'restauration';
            setClientType(type);
            setTvaRates(tauxParDefaut(type));
            if (data?.conditions) { setConditions(data.conditions); setConditionsResume(data.conditions_resume || ''); }
            setModulesList(Array.isArray(data?.modules) ? data.modules : []);
            setDocs(Object.fromEntries((Array.isArray(data?.documents) ? data.documents : []).map((d: any) => [d.kind, d.filename])));
            setPhase('form');
        }).catch(() => setPhase('invalid'));
    }, [token]);

    const handleLogoChange = async (file: File | null) => {
        setLogoFile(file);
        if (!file || !token) return;
        setUploadingLogo(true);
        setError('');
        try {
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = `${reader.result ?? ''}`;
                    resolve(result.includes(',') ? result.split(',')[1] : result);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const upload: any = await registrationApi.uploadLogo(token, base64, file.type || 'image/png');
            if (upload?.logo_url) setLogoUrl(upload.logo_url);
            else setError(upload?.error || 'Erreur lors du téléversement du logo');
        } catch (e: any) {
            setError(e?.message || 'Erreur lors du téléversement du logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleDocument = async (kind: string, file: File | null) => {
        if (!file || !token) return;
        setError('');
        if (file.size > 2 * 1024 * 1024) { setError('Fichier trop volumineux (2 Mo maximum).'); return; }
        setUploadingDoc(kind);
        try {
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => { const r = `${reader.result ?? ''}`; resolve(r.includes(',') ? r.split(',')[1] : r); };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const res: any = await registrationApi.uploadDocument(token, kind, file.name, base64, file.type);
            if (res?.success) setDocs((prev) => ({ ...prev, [kind]: res.filename || file.name }));
            else setError(res?.error || "Erreur lors de l'envoi du document");
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'envoi du document");
        } finally {
            setUploadingDoc('');
        }
    };

    const toggleModule = (m: ModuleProposeForm) => {
        setSelModules((prev) => {
            if (prev.includes(m.name)) {
                // On retire aussi ce qui ne peut pas rester sans lui.
                return prev.filter((n) => n !== m.name && !(modulesList.find((x) => x.name === n)?.avec || []).includes(m.name));
            }
            const aAjouter = [m.name, ...m.avec.filter((n) => modulesList.some((x) => x.name === n && x.selectionnable))];
            return [...new Set([...prev, ...aAjouter])];
        });
    };
    // Pendant un pilote, tout module qui a un prix est offert, sauf ceux que L'IAmani a laissés payants.
    const offertPilote = (m: ModuleProposeForm) => estPilote && prixMensuel(m) > 0 && !(conditions.pilote?.payants || []).includes(m.name);
    const libelleModule = (m: ModuleProposeForm) => (offertPilote(m) ? 'Offert pendant le pilote' : libellePrix(m));
    const totalMensuel = modulesList.filter((m) => selModules.includes(m.name)).reduce((s, m) => s + prixMensuel(m), 0);
    const totalPendantPilote = modulesList.filter((m) => selModules.includes(m.name) && !offertPilote(m)).reduce((s, m) => s + prixMensuel(m), 0);
    const totalAPartirDe = modulesList.some((m) => selModules.includes(m.name) && m.mode === 'paliers');

    const toggleTvaRate =(v: number) => setTvaRates((prev) => (prev.includes(v) ? prev.filter((r) => r !== v) : [...prev, v].sort((a, b) => b - a)));

    // Un prestataire qui intervient chez des particuliers doit leur garantir un médiateur de la consommation (Code de la consommation).
    const mediateurObligatoire = clientType === 'prestation' && locationMode !== 'entreprise';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;
        setError('');
        if (!fullName.trim() || !companyName.trim() || !contactEmail.trim()) {
            setError('Merci de remplir tous les champs obligatoires.');
            return;
        }
        if (clientType === 'fournisseur' && categories.length === 0 && !(autreActif && autreTexte.trim())) {
            setError(autreActif ? 'Précisez en quelques mots ce que vous vendez.' : 'Indiquez ce que vous vendez : au moins une catégorie, ou « Autre ».');
            return;
        }
        if (clientType === 'fournisseur' && autreActif && !autreTexte.trim()) {
            setError('Précisez en quelques mots ce que vous vendez dans « Autre », ou décochez-le.');
            return;
        }
        if (mediateurObligatoire && !mediateur.trim() && !mediateurPlusTard) {
            setError("Indiquez votre médiateur de la consommation, ou cochez « Je n'en ai pas encore » : il sera à renseigner avant votre premier contrat avec un particulier.");
            return;
        }
        if (!tvaFranchise && tvaRates.length === 0) {
            setError('Choisissez au moins un taux de TVA, ou cochez la franchise en base.');
            return;
        }
        if (!tvaConfirmed) {
            setError('Confirmez que vos taux de TVA correspondent à votre activité.');
            return;
        }
        if (estPilote && !conventionOk) {
            setError('Acceptez la convention de client pilote pour envoyer votre demande.');
            return;
        }
        const manquants = DOCUMENTS.filter((d) => d.obligatoire && !docs[d.kind]);
        if (manquants.length) {
            setError(`Ajoutez les documents obligatoires : ${manquants.map((d) => d.label).join(', ')}.`);
            return;
        }
        setSubmitting(true);
        try {
            await registrationApi.submit(token, {
                modules: selModules, convention_confirmed: estPilote ? conventionOk : undefined,
                full_name: fullName, company_name: companyName, contact_email: contactEmail,
                logo_url: logoUrl || null, tva_rates: tvaFranchise ? [] : tvaRates, tva_franchise: tvaFranchise, tva_confirmed: tvaConfirmed,
                address, postal_code: postalCode, city, country, siret, tva, phone,
                capital, ape, siege_social: siegeSocial, rcs_ville: rcsVille, rcs_numero: rcsNumero, mediateur: mediateur.trim(), mediateur_plus_tard: mediateurPlusTard,
                ...(clientType === 'prestation' ? { prestation_location_mode: locationMode } : {}),
                ...(clientType === 'fournisseur' ? { fournisseur_profil: { categories, autre: autreActif ? autreTexte.trim() : '', zone, minimum_ht: minimumHt.trim() === '' ? null : Number(minimumHt.replace(',', '.')), jours_livraison: joursLivraison, delai_heures: delaiHeures.trim() === '' ? null : Number(delaiHeures) } } : {}),
            });
            setPhase('submitted');
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'envoi de la demande");
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass = "w-full bg-surface border border-border rounded-lg px-3 py-2 text-content text-sm outline-none focus:border-brand-500 transition-colors";
    const labelClass = "block text-xs font-bold text-content-muted uppercase mb-1";

    if (phase === 'loading') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-app">
                <Loader2 className="animate-spin text-content-muted" size={32} />
            </div>
        );
    }

    if (phase === 'invalid' || phase === 'expired' || phase === 'already') {
        const messages = {
            invalid: { icon: <XCircle className="text-red-500" size={40} />, title: 'Lien invalide', text: "Ce lien d'inscription n'existe pas." },
            expired: { icon: <XCircle className="text-red-500" size={40} />, title: 'Lien expiré', text: "Ce lien d'inscription n'est plus valide. Merci de contacter L'IAmani pour en recevoir un nouveau." },
            already: { icon: <CheckCircle2 className="text-brand-500" size={40} />, title: 'Demande déjà traitée', text: 'Cette demande d\'inscription a déjà été soumise et traitée.' },
        } as const;
        const m = messages[phase];
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-app p-4">
                <div className="bg-surface border border-border rounded-2xl p-10 max-w-md w-full text-center space-y-4">
                    <div className="flex justify-center">{m.icon}</div>
                    <h1 className="text-xl font-black text-content">{m.title}</h1>
                    <p className="text-sm text-content-muted">{m.text}</p>
                </div>
            </div>
        );
    }

    if (phase === 'submitted') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-app p-4">
                <div className="bg-surface border border-border rounded-2xl p-10 max-w-md w-full text-center space-y-4">
                    <div className="flex justify-center"><CheckCircle2 className="text-green-500" size={40} /></div>
                    <h1 className="text-xl font-black text-content">Demande envoyée</h1>
                    <p className="text-sm text-content-muted">Votre demande d'inscription a bien été transmise à L'IAmani. Vous recevrez un email dès qu'elle aura été traitée.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-surface-app py-10 px-4">
            <div className="max-w-2xl mx-auto">
                <div className="flex items-center gap-3 mb-8 justify-center">
                    <div className="w-12 h-12 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500">
                        <Building2 size={22} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-content">Demande d'inscription</h1>
                        <p className="text-sm text-content-muted">{clientType === 'fournisseur' ? 'Fournisseur de produits' : clientType === 'commercial' ? 'Entreprise commerciale (démarchage pour des mandants)' : `Client ${clientType === 'prestation' ? 'Prestation de service' : clientType === 'hotellerie' ? 'Hôtellerie' : 'Restauration'}`}</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="bg-surface border border-border rounded-2xl p-8 space-y-6">
                    {error && <div className="bg-red-500/10 border border-red-500/30 text-red-500 text-sm rounded-lg px-3 py-2">{error}</div>}

                    <div>
                        <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-3">Informations générales</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>Nom complet *</label>
                                <input value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputClass} placeholder="Prénom Nom" required />
                            </div>
                            <div>
                                <label className={labelClass}>Nom de l'établissement *</label>
                                <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={inputClass} required />
                            </div>
                            <div>
                                <label className={labelClass}>Email *</label>
                                <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className={inputClass} required />
                            </div>
                            <div>
                                <label className={labelClass}>Votre identifiant de connexion</label>
                                <p className="text-sm text-content">{slugEtablissement(companyName) ? <b className="font-mono">{slugEtablissement(companyName)}.direction</b> : "Il sera créé à partir du nom de votre établissement."}</p>
                                <p className="text-xs text-content-muted mt-1">Généré automatiquement à partir du nom de l'établissement. Vous le recevrez par e-mail.</p>
                            </div>
                        </div>
                    </div>

                    {clientType === 'prestation' && (
                        <div>
                            <label className={labelClass}>Mode d'intervention</label>
                            <div className="grid grid-cols-3 gap-2">
                                {([
                                    { key: 'domicile', label: 'Chez le client' },
                                    { key: 'entreprise', label: "Dans l'entreprise" },
                                    { key: 'les_deux', label: 'Les deux' },
                                ] as const).map((opt) => (
                                    <button key={opt.key} type="button" onClick={() => setLocationMode(opt.key)}
                                        className={`px-3 py-2.5 rounded-lg text-xs font-bold border transition-all ${locationMode === opt.key ? 'border-brand-500/40 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted'}`}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {clientType === 'fournisseur' && (
                        <div className="space-y-4">
                            <div>
                                <label className={labelClass}>Ce que vous vendez *</label>
                                <div className="flex flex-wrap gap-2">
                                    {CATEGORIES_FOURNISSEUR.map((c: string) => (
                                        <button key={c} type="button" aria-pressed={categories.includes(c)} onClick={() => setCategories((l) => basculer(l, c))}
                                            className={`px-3 py-2 rounded-lg text-xs font-bold border transition-all ${categories.includes(c) ? 'border-brand-500/40 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted'}`}>{c}</button>
                                    ))}
                                    <button type="button" aria-pressed={autreActif} onClick={() => setAutreActif((v) => !v)}
                                        className={`px-3 py-2 rounded-lg text-xs font-bold border transition-all ${autreActif ? 'border-brand-500/40 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted'}`}>Autre</button>
                                </div>
                                {autreActif && (
                                    <input value={autreTexte} onChange={(e) => setAutreTexte(e.target.value)} maxLength={120} autoFocus aria-label="Ce que vous vendez, en quelques mots"
                                        className={`${inputClass} mt-2`} placeholder="Précisez en quelques mots : ex. matériel de tatouage" />
                                )}
                                <p className="text-[11px] text-content-muted mt-1">Ces catégories servent à vous présenter aux bons clients, par métier.</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Zone de livraison</label>
                                    <input value={zone} onChange={(e) => setZone(e.target.value)} className={inputClass} placeholder="Ex. Bouches-du-Rhône, Var" maxLength={200} />
                                </div>
                                <div>
                                    <label className={labelClass}>Commande minimum (HT, en €)</label>
                                    <input inputMode="decimal" value={minimumHt} onChange={(e) => setMinimumHt(e.target.value)} className={inputClass} placeholder="Ex. 150" />
                                </div>
                                <div>
                                    <label className={labelClass}>Délai de préparation (heures)</label>
                                    <input inputMode="numeric" value={delaiHeures} onChange={(e) => setDelaiHeures(e.target.value)} className={inputClass} placeholder="Ex. 24" />
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>Jours de livraison</label>
                                <div className="flex flex-wrap gap-2">
                                    {JOURS_LIVRAISON.map((j: string) => (
                                        <button key={j} type="button" aria-pressed={joursLivraison.includes(j)} onClick={() => setJoursLivraison((l) => basculer(l, j))}
                                            className={`px-3 py-2 rounded-lg text-xs font-bold border transition-all ${joursLivraison.includes(j) ? 'border-brand-500/40 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted'}`}>{j}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className={labelClass} htmlFor="inscription-mediateur">Médiateur de la consommation{mediateurObligatoire ? ' *' : ''}</label>
                        <input id="inscription-mediateur" value={mediateur} onChange={(e) => setMediateur(e.target.value)} className={inputClass}
                            placeholder="Nom, adresse postale et site du médiateur" maxLength={300} disabled={mediateurPlusTard} />
                        {mediateurObligatoire && (
                            <label className="flex items-center gap-2 mt-1.5 text-xs text-content cursor-pointer">
                                <input type="checkbox" checked={mediateurPlusTard} onChange={(e) => { setMediateurPlusTard(e.target.checked); if (e.target.checked) setMediateur(''); }} />
                                Je n'en ai pas encore : je le renseignerai avant mon premier contrat avec un particulier.
                            </label>
                        )}
                        <p className="text-[11px] text-content-muted mt-1">
                            {mediateurObligatoire
                                ? 'Vos clients particuliers doivent pouvoir y recourir gratuitement. Il sera repris dans vos conditions de vente, vos devis et vos contrats.'
                                : 'Facultatif si vous ne vendez pas à des particuliers. Vous pourrez le renseigner plus tard dans vos réglages.'}
                            {' '}Vous n'en avez pas encore ? Adhérez à un médiateur agréé (liste officielle sur economie.gouv.fr, rubrique « médiation de la consommation »).
                        </p>
                    </div>

                    <div>
                        <label className={labelClass}>Logo de l'établissement</label>
                        <div className="flex items-center gap-3">
                            <div className="w-16 h-16 rounded-xl border border-border bg-surface-soft flex items-center justify-center overflow-hidden shrink-0">
                                {logoUrl ? <img src={logoUrl} alt="" className="w-full h-full object-cover" /> : <Upload size={18} className="text-content-muted" />}
                            </div>
                            <label className="cursor-pointer px-4 py-2 rounded-lg border border-border bg-surface-soft text-content text-sm font-semibold hover:border-brand-500">
                                {uploadingLogo ? 'Envoi...' : logoFile ? logoFile.name : 'Choisir un fichier'}
                                <input type="file" accept="image/png,image/jpeg,image/gif" className="hidden" onChange={(e) => handleLogoChange(e.target.files?.[0] || null)} disabled={uploadingLogo} />
                            </label>
                        </div>
                        <p className="text-[11px] text-content-muted mt-1">Formats acceptés : PNG, JPG, GIF (max 1 Mo).</p>
                    </div>

                    <div>
                        <label className={labelClass}>Taux de TVA appliqués</label>
                        <div className="flex flex-wrap gap-2 mb-2">
                            {TAUX_TVA_LEGAUX.map((r: number) => {
                                const actif = !tvaFranchise && tvaRates.includes(r);
                                return (
                                    <button key={r} type="button" onClick={() => toggleTvaRate(r)} disabled={tvaFranchise} aria-pressed={actif}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-bold border transition-all disabled:opacity-40 ${actif ? 'border-brand-500/40 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted'}`}>
                                        {String(r).replace('.', ',')} %
                                    </button>
                                );
                            })}
                        </div>
                        <label className="flex items-start gap-2 text-sm text-content mb-2">
                            <input type="checkbox" checked={tvaFranchise} onChange={(e) => setTvaFranchise(e.target.checked)} className="mt-1" />
                            <span>Je suis en franchise en base : je ne facture pas de TVA (mention « TVA non applicable, art. 293 B du CGI » sur mes factures).</span>
                        </label>
                        <p className="text-[11px] text-content-muted mb-2">
                            Ces taux sont ceux fixés par la loi. Cochez ceux qui s'appliquent à votre activité ; en cas de doute, demandez à votre comptable.
                            Ils servent ensuite à vos devis et factures et pourront être modifiés dans vos réglages.
                        </p>
                        <label className="flex items-start gap-2 text-sm font-semibold text-content">
                            <input type="checkbox" checked={tvaConfirmed} onChange={(e) => setTvaConfirmed(e.target.checked)} className="mt-1" />
                            <span>Je confirme que ces taux de TVA correspondent à mon activité, et je reste seul responsable des taux et des mentions de TVA de mes devis et factures. *</span>
                        </label>
                    </div>

                    <div>
                        <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-3">Informations légales</h2>
                        <div className="space-y-4">
                            <div>
                                <label className={labelClass}>Rue / Adresse postale</label>
                                <input value={address} onChange={(e) => setAddress(e.target.value)} className={inputClass} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Code Postal</label>
                                    <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Ville</label>
                                    <input value={city} onChange={(e) => setCity(e.target.value)} className={inputClass} />
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>Pays</label>
                                <input value={country} onChange={(e) => setCountry(e.target.value)} className={inputClass} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>SIRET</label>
                                    <input value={siret} onChange={(e) => setSiret(e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>TVA Intracommunautaire</label>
                                    <input value={tva} onChange={(e) => setTva(e.target.value)} className={inputClass} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Téléphone</label>
                                    <input value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Capital Social</label>
                                    <input value={capital} onChange={(e) => setCapital(e.target.value)} className={inputClass} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Code APE / NAF</label>
                                    <input value={ape} onChange={(e) => setApe(e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Siège Social (si différent)</label>
                                    <input value={siegeSocial} onChange={(e) => setSiegeSocial(e.target.value)} className={inputClass} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Ville d'immatriculation RCS</label>
                                    <input value={rcsVille} onChange={(e) => setRcsVille(e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Numéro RCS</label>
                                    <input value={rcsNumero} onChange={(e) => setRcsNumero(e.target.value)} className={inputClass} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-1">Documents</h2>
                        <p className="text-[11px] text-content-muted mb-3">Ces pièces servent uniquement à vérifier votre entreprise. PDF, JPG ou PNG, 2 Mo maximum chacune.</p>
                        <div className="space-y-2">
                            {DOCUMENTS.map((d) => (
                                <label key={d.kind} className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${docs[d.kind] ? 'border-green-500/40 bg-green-500/5' : 'border-dashed border-border hover:border-brand-500'}`}>
                                    <span className="w-9 h-9 rounded-lg bg-surface-soft flex items-center justify-center text-content-muted shrink-0">{docs[d.kind] ? <CheckCircle2 size={18} className="text-green-500" /> : <Upload size={16} />}</span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-bold text-content">{d.label}{d.obligatoire ? ' *' : ''}</span>
                                        <span className="block text-[11px] text-content-muted truncate">{uploadingDoc === d.kind ? 'Envoi en cours…' : docs[d.kind] ? `${docs[d.kind]} · ajouté` : d.aide}</span>
                                    </span>
                                    <span className="text-xs font-semibold text-content-muted">{docs[d.kind] ? 'Remplacer' : 'Ajouter'}</span>
                                    <input type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" disabled={!!uploadingDoc}
                                        onChange={(e) => { handleDocument(d.kind, e.target.files?.[0] || null); e.target.value = ''; }} />
                                </label>
                            ))}
                        </div>
                    </div>

                    {modulesList.length > 0 && (
                        <div>
                            <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-1">Vos modules</h2>
                            <p className="text-[11px] text-content-muted mb-3">Cochez ce dont vous avez besoin. Les prix sont par mois, hors taxes. Vous pourrez demander d'autres modules plus tard.</p>
                            <div className="space-y-1.5">
                                {modulesList.filter((m) => m.core).map((m) => (
                                    <div key={m.name} className="flex items-center gap-3 rounded-xl border border-border bg-surface-soft px-4 py-2.5">
                                        <CheckCircle2 size={16} className="text-green-500 shrink-0" />
                                        <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-content">{m.label}</span><span className="block text-[11px] text-content-muted">{m.description}</span></span>
                                        <span className="text-xs font-semibold text-content-muted">Inclus</span>
                                    </div>
                                ))}
                                {modulesList.filter((m) => !m.core && m.selectionnable).map((m) => {
                                    const actif = selModules.includes(m.name);
                                    return (
                                        <button key={m.name} type="button" onClick={() => toggleModule(m)} aria-pressed={actif}
                                            className={`w-full flex items-center gap-3 rounded-xl border px-4 py-2.5 text-left transition-colors ${actif ? 'border-brand-500 bg-brand-500/10' : 'border-border hover:border-brand-500/50'}`}>
                                            <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 text-[11px] font-black ${actif ? 'bg-brand-500 border-brand-500 text-brand-fg' : 'border-border'}`}>{actif ? '✓' : ''}</span>
                                            <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-content">{m.label}</span><span className="block text-[11px] text-content-muted">{m.description}</span></span>
                                            <span className={`text-xs font-bold whitespace-nowrap ${offertPilote(m) ? 'text-green-600' : 'text-content'}`}>{libelleModule(m)}</span>
                                        </button>
                                    );
                                })}
                                {modulesList.some((m) => !m.core && !m.selectionnable) && <p className="text-[11px] font-bold uppercase tracking-wider text-content-muted pt-2">Bientôt disponibles</p>}
                                {modulesList.filter((m) => !m.core && !m.selectionnable).map((m) => (
                                    <div key={m.name} className="flex items-center gap-3 rounded-xl border border-border px-4 py-2.5 opacity-60">
                                        <span className="w-5 h-5 rounded-md border border-border shrink-0" />
                                        <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-content">{m.label}</span><span className="block text-[11px] text-content-muted">{m.description}</span></span>
                                        <span className="text-xs font-semibold text-content-muted">Bientôt</span>
                                    </div>
                                ))}
                            </div>
                            {estPilote ? (
                                <div className="border-t border-border mt-3 pt-3 space-y-1">
                                    <div className="flex items-baseline justify-between"><span className="text-sm text-content-muted">Pendant le pilote ({conditions.pilote?.mois} mois), par mois, hors taxes</span><b className="text-xl text-green-600">{euros(totalPendantPilote)}</b></div>
                                    <div className="flex items-baseline justify-between"><span className="text-sm text-content-muted">Après le pilote, par mois, hors taxes{totalAPartirDe ? ' (à partir de)' : ''}</span><b className="text-content">{euros(totalMensuel)}</b></div>
                                </div>
                            ) : (
                                <div className="border-t border-border mt-3 pt-3 space-y-1">
                                    <div className="flex items-baseline justify-between"><span className="text-sm text-content-muted">Total par mois, hors taxes{totalAPartirDe ? ' (à partir de)' : ''}</span><b className="text-xl text-content">{euros(totalMensuel)}</b></div>
                                    {conditions.offre && <p className="text-xs font-semibold text-green-600">Offre pour vous : {conditionsResume}, appliquée à votre facture.</p>}
                                </div>
                            )}
                        </div>
                    )}

                    {estPilote && (
                        <div>
                            <h2 className="text-xs font-black uppercase tracking-wider text-content-muted mb-2">Convention de client pilote</h2>
                            <div className="rounded-xl bg-surface-soft border border-border p-4 text-sm text-content space-y-1">
                                <p>Pendant {conditions.pilote?.mois} mois, vous utilisez la plateforme avec la plupart des modules offerts. En échange :</p>
                                <ul className="list-disc pl-5 text-content-muted space-y-0.5">
                                    <li>vous testez en situation réelle et nous signalez les bugs et ce qui manque ;</li>
                                    <li>vous répondez à un point de suivi régulier ;</li>
                                    <li>les fonctionnalités peuvent évoluer ou être corrigées sans préavis, et vous vérifiez vos documents avant de les utiliser ;</li>
                                    <li>vos retours restent confidentiels, et la plateforme n'est pas citée publiquement sans votre accord ;</li>
                                    <li>à la fin du pilote, vous passez en formule payante ou vous quittez la plateforme, et vos données vous sont rendues.</li>
                                </ul>
                            </div>
                            <label className="flex items-start gap-2 text-sm font-semibold text-content mt-3">
                                <input type="checkbox" checked={conventionOk} onChange={(e) => setConventionOk(e.target.checked)} className="mt-1" />
                                <span>J'accepte la convention de client pilote. *</span>
                            </label>
                        </div>
                    )}

                    <button type="submit" disabled={submitting || uploadingLogo || !!uploadingDoc}
                        className="w-full py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold shadow-lg disabled:opacity-50">
                        {submitting ? 'Envoi...' : 'Envoyer la demande'}
                    </button>
                </form>
            </div>
        </div>
    );
};
